import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import OpText from 'components/Common/op-ui-wrappers/OpText/OpText';
import isDesktop from 'store/app/selector/isDesktop';
import PlusMembershipInfoCta from 'components/Plus/PlusMembershipInfo/PlusMembershipInfoCta';
import {
  PlusMembershipInfoItem,
  UpdateMembershipItem,
} from 'components/Plus/PlusMembershipInfo/PlusMembershipInfoItem';
import usePlusMembershipStatusData from 'components/Plus/hooks/useMembershipStatusData';
import PlusAccordion from 'components/Plus/PlusAccordion/PlusAccordion';
import usePlusMembershipStatus from 'components/Plus/hooks/usePlusMembershipStatus';
import getOpPlusV3Info from 'store/dashboard/selector/getOpPlusV3Info';
import styles from './PlusMembershipInfo.module.scss';

export type PlusMembershipInfoProps = {
  onCancelMembership: () => void;
  onRepurchaseMembership?: () => void;
  onContactCustomerSupport?: () => void;
};

const PlusMembershipInfo: FC<PlusMembershipInfoProps> = ({
  onCancelMembership,
  onRepurchaseMembership,
  onContactCustomerSupport,
}) => {
  const isDesktopDevice = useSelector(isDesktop);
  const { t } = useTranslation();
  const subscriptionData = useSelector(getOpPlusV3Info);
  const {
    isCancelled,
    isCancelledBeforeRenewal,
    isMembershipWithdrawn,
    isLegacyOpPlus,
    legacyExpirationDays,
    isFailedPaymentCancelled,
  } = usePlusMembershipStatus();

  if (!subscriptionData) return null;

  // TODO: Implement this when we have Receipts API ready
  // const onSeeReceiptsHandler = () => {
  //   console.log('See all your Plus receipts');
  // };

  const status = usePlusMembershipStatusData();
  const { isFailedPaymentPausedFirst30Days } = usePlusMembershipStatus();

  let membershipInfoTitle =
    subscriptionData?.subscriptionInfo.status === 'non-renewing' ? t('txt_plus_end_date') : t('txt_plus_renewal_date');
  let membershipInfoLabel = subscriptionData?.subscriptionInfo.renewalDateFormatted;

  if (isMembershipWithdrawn) {
    membershipInfoTitle = t('txt_plus_ended_on');
    membershipInfoLabel = subscriptionData?.subscriptionInfo.cancelDateFormatted;
  }

  if (isFailedPaymentCancelled) {
    membershipInfoTitle = t('txt_plus_ended_on');
    membershipInfoLabel = subscriptionData?.subscriptionInfo.cancelDateFormatted;
  }

  return (
    <>
      <OpText variant={isDesktopDevice ? 'title-large-bold' : 'title-medium-bold'} className={styles.title}>
        {t('txt_plus_membership_info')}
      </OpText>
      <div className={styles.container}>
        <div className={styles.membershipInfo}>
          <PlusAccordion
            activeItemLabel={t('txt_plus_payment_info')}
            activeItem={<PlusMembershipInfoItem title={membershipInfoTitle} label={membershipInfoLabel} />}
            hiddenItems={
              <>
                <PlusMembershipInfoItem
                  title={t('txt_last_payment')}
                  label={subscriptionData?.subscriptionInfo.startDateFormatted}
                />
              </>
            }
          />
        </div>
        <div className={styles.manageMembership}>
          <PlusAccordion
            activeItemLabel={t('txt_plus_update')}
            activeItem={<PlusMembershipInfoItem title={`${t('txt_plus_manage')}`} label={t('txt_plus_membership')} />}
            hiddenItems={
              <UpdateMembershipItem
                status={status}
                hasContactCustomerSupport={isFailedPaymentPausedFirst30Days}
                cta={
                  <PlusMembershipInfoCta
                    {...{
                      isCancelled,
                      isCancelledBeforeRenewal,
                      isLegacyOpPlus,
                      legacyExpirationDays,
                      onCancelMembership,
                      onRepurchaseMembership,
                      onContactCustomerSupport,
                      t,
                    }}
                  />
                }
              />
            }
          />
        </div>
      </div>
    </>
  );
};

export default PlusMembershipInfo;
